import React, {
  useState, ReactElement, useRef, useEffect,
} from 'react';
import PasswordInput from '../halda-design-system/inputs/PasswordInput';

interface LogInScreenProps {
  newPasswordPath: string;
  setIsRememberable: React.Dispatch<React.SetStateAction<boolean>>;
  userEmail: string;
  setUserEmail: React.Dispatch<React.SetStateAction<string>>;
  userPassword: string;
  setUserPassword: React.Dispatch<React.SetStateAction<string>>;
  setIsLoggingInFn: React.Dispatch<React.SetStateAction<boolean>>;
  setUserPhone: React.Dispatch<React.SetStateAction<string>>;
  setSentCode: React.Dispatch<React.SetStateAction<boolean>>;
  setFirstName: React.Dispatch<React.SetStateAction<string>>;
  setIsTwoFactorRequired: React.Dispatch<React.SetStateAction<boolean>>;
  setIsMfaForcedByOrgAdmin: React.Dispatch<React.SetStateAction<boolean>>;
  setMfaMethod: React.Dispatch<React.SetStateAction<string>>;
}

const LogInScreen = ({
  newPasswordPath,
  setIsRememberable,
  userEmail,
  setUserEmail,
  userPassword,
  setUserPassword,
  setIsLoggingInFn,
  setUserPhone,
  setSentCode,
  setFirstName,
  setIsTwoFactorRequired,
  setIsMfaForcedByOrgAdmin,
  setMfaMethod,
}: LogInScreenProps): ReactElement => {
  const [showPassword, setShowPassword] = useState(false);
  const [showErrorMsg, setShowErrorMsg] = useState(false);
  const [loginErrorMsg, setLoginErrorMsg] = useState('');

  const handleContinueClick = () => {
    if (userEmail != null && userEmail.length > 0) {
      setShowErrorMsg(false);
      setShowPassword(true);
    } else {
      setShowErrorMsg(true);
    }
  };

  const handleKeyboardButton = (
    e: React.KeyboardEvent<HTMLInputElement>, isEmailInput: boolean,
  ) => {
    if (e.key === 'Enter') {
      if (isEmailInput && !showPassword) {
        e.preventDefault();
        handleContinueClick();
        return;
      }

      handleSignInClick();
    }
  };

  const passwordInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (showPassword) passwordInputRef?.current?.focus();
  }, [showPassword]);

  const handleSignInClick = () => {
    if (userPassword?.length === 0
      || userPassword === null
      || userEmail?.length === 0
      || userEmail === null
    ) {
      return;
    }
    const isRememberableChecked = (document.getElementById('user_remember_me') as HTMLInputElement).checked;
    setIsRememberable(isRememberableChecked);
    const fetchOptions = {
      method: 'POST',
      body: JSON.stringify({
        username: userEmail,
        password: userPassword,
      }),
      headers: { 'Content-Type': 'application/json; charset=UTF-8' },
    };
    fetch('/authenticate/verify-user', fetchOptions)
      .then(resp => resp.json())
      .then(jsonResp => {
        if (jsonResp.success) {
          setUserPhone(jsonResp.phone_number);
          setFirstName(jsonResp.first_name);
          setIsTwoFactorRequired(jsonResp.two_factor_required);
          setMfaMethod(jsonResp.mfa_method || 'sms');
          if (jsonResp.two_factor_required) {
            setSentCode(true);
          }
          setIsMfaForcedByOrgAdmin(jsonResp.is_mfa_forced_by_org_admin);
          setLoginErrorMsg('');
          setIsLoggingInFn(false);
        } else {
          setLoginErrorMsg(jsonResp.error);
        }
      });
  };

  return (
    <div className="space-y-6">
      <h2 className="text-left text-3xl font-semibold text-gray-900">
        Sign In
      </h2>
      <div>
        <label htmlFor="user_email" className="block text-sm font-medium text-gray-700">Email</label>
        <div className="mt-1">
          <input
            id="user_email"
            type="email"
            name="user[email]"
            required={true}
            onKeyDown={(e) => handleKeyboardButton(e, true)}
            onChange={(e) => setUserEmail(e.target.value)}
            autoFocus={true}
            autoComplete="username"
            defaultValue={userEmail || ''}
            className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm"
          />
        </div>
      </div>
      <div style={{ display: showPassword ? "block" : "none" }}>
        <label htmlFor="user_password" className="block text-sm font-medium text-gray-700 mb-1">Password</label>
        <PasswordInput id="user_password" name="user[password]" autoComplete="current-password" ref={passwordInputRef} onChange={(e) => setUserPassword(e.target.value)} handleKeyboardButton={handleKeyboardButton} />
      </div>
      {!showPassword ? (
        <>
          {showErrorMsg && <div className="block text-sm font-medium text-red-500">Please enter an email to continue</div>}
          <div>
            <button
              type="button"
              onClick={handleContinueClick}
              className="bg-gradient-to-t from-blue-600 to-blue-500 cursor-pointer w-full h-14 flex justify-center py-2 px-4 border border-blue-600 rounded-md drop-shadow-xl text-md font-medium text-white focus:outline-none hover:bg-blue-500"
            >
              <span className="my-auto">Continue</span>
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="flex flex-col sm:flex-row items-left sm:items-center justify-between">
            <div className="flex items-center">
              <label className="block text-sm text-gray-900">
                <input id="user_remember_me" type="checkbox" name="user[remember_me]" className="h-4 w-4 border-gray-300 rounded" />
                <span className="ml-2">Remember for 30 days</span>
              </label>
            </div>
            <div className="text-sm mt-2 sm:mt-0">
              <a href={newPasswordPath} className="font-medium text-blue-600 hover:text-blue-600-600">Forgot your password?</a>
            </div>
          </div>
          {loginErrorMsg.length > 0 && <div className="block text-sm font-medium text-red-500">{loginErrorMsg}</div>}
          <div>
            <button
              cy-data="sign-in-button"
              type="button"
              onClick={handleSignInClick}
              className="bg-gradient-to-t from-blue-600 to-blue-500 cursor-pointer w-full h-14 flex justify-center py-2 px-4 border border-blue-600 rounded-md drop-shadow-xl text-md font-medium text-white focus:outline-none hover:bg-blue-500"
            >
              <span className="my-auto">Sign In</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default LogInScreen;
